import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import mqtt from 'mqtt';
import { Flex, Tree, Typography, Select, Tooltip } from 'antd';
import './Uns.css';
import { Content } from 'antd/es/layout/layout';

const { DirectoryTree } = Tree;
const { Title } = Typography;

// Throttle tree updates to this interval (ms)
const UPDATE_THROTTLE_MS = 500;
// How long to highlight changed values (ms)
const HIGHLIGHT_DURATION_MS = 1500;

// A standalone UNS tree sidebar that shows MQTT data
// Can be embedded in any page without the UnsFlow graph
// All config is now passed via props from Firestore unsConfig — no hard-coded fallbacks.
const EMPTY_ARRAY = [];
const EMPTY_CONFIG = {};

const UnsSidebar = ({
  unsId,
  mqttConfigOverride = null,
  siteFilters: siteFiltersProp = EMPTY_ARRAY,
  topicBranches: topicBranchesProp = EMPTY_ARRAY,
  defaultFilter: defaultFilterProp = 'overall',
  defaultExpandDepth = Infinity,
}) => {
  // MQTT connection config — passed from Firestore unsConfig.mqttConfig
  const currentConfig = mqttConfigOverride || EMPTY_CONFIG;

  // Stabilize array references to prevent unnecessary MQTT reconnections
  const stableTopicBranches = useMemo(() => topicBranchesProp, [JSON.stringify(topicBranchesProp)]);
  const stableSiteFilters = useMemo(() => siteFiltersProp, [JSON.stringify(siteFiltersProp)]);
  const stableTopicPrefix = currentConfig.topicPrefix || '';

  const effectiveSiteFilters = stableSiteFilters;
  const hasFilters = effectiveSiteFilters.length > 0;
  const effectiveDefaultFilter = defaultFilterProp;

  const [antTree, setAntTree] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedSite, setSelectedSite] = useState(effectiveDefaultFilter);
  
  // Use refs for data that doesn't need to trigger re-renders
  const messageTreeRef = useRef({});
  const previousValuesRef = useRef({}); // Track previous values to detect changes
  const recentlyChangedRef = useRef(new Map()); // Map of path -> timestamp when it changed
  const pendingUpdateRef = useRef(null);
  const clientRef = useRef(null);
  const expandedKeysRef = useRef([]);
  const isInitializedRef = useRef(false);
  const highlightTimeoutRef = useRef(null);
  const userCollapsedKeysRef = useRef(new Set());

  // Build topic based on selected site and Firestore unsConfig
  const topic = useMemo(() => {
    const prefix = stableTopicPrefix.replace(/\/+$/, '').replace(/^[#+/\s]+$/, '');
    const joinTopic = (suffix) => prefix ? `${prefix}/${suffix}` : suffix;

    const branches = stableTopicBranches;

    // If a site filter is selected (not 'overall'), use its topicPattern
    if (selectedSite && selectedSite !== 'overall') {
      const filter = effectiveSiteFilters.find((f) => f.value === selectedSite);
      if (filter?.topicPattern) {
        return [joinTopic(filter.topicPattern)];
      }
      return [];
    }

    // Otherwise subscribe to all branches
    return branches.map((b) => joinTopic(b));
  }, [selectedSite, stableTopicPrefix, stableTopicBranches, effectiveSiteFilters]);

  function isEmptyObject(obj) {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  }

  // Merge objects deeply (mutates target for performance)
  const mergeDeep = useCallback((target = {}, source = {}) => {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (source[key] instanceof Object && key in target && target[key] instanceof Object) {
          mergeDeep(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      }
    }
    return target;
  }, []);

  // Helper function to truncate text with ellipsis based on available space
  // Simple approach: fixed max chars for value, adjusted slightly by depth
  const truncateValue = useCallback((value, depth, keyLength = 0) => {
    // Simple calculation: more depth = less space, longer key = less space
    // Start with 18 chars for value, subtract 1 per depth level and factor in key length
    const baseValueChars = 18;
    const depthPenalty = Math.floor(depth * 1.5);
    const keyPenalty = Math.max(0, keyLength - 8); // penalize keys longer than 8 chars
    
    const maxValueChars = Math.max(6, baseValueChars - depthPenalty - Math.floor(keyPenalty / 2));
    
    if (value.length <= maxValueChars) {
      return { displayValue: value, isTruncated: false };
    }
    
    // Show at least 4 chars before "..."
    const charsToShow = Math.max(4, maxValueChars - 3);
    return { displayValue: value.substring(0, charsToShow) + '...', isTruncated: true };
  }, []);

  // Convert message tree to Ant Design tree format
  // Collects non-leaf keys for expansion (up to defaultExpandDepth)
  // Tracks changed values for highlighting
  // Uses path-based keys for stability across updates
  const convertToTreeData = useCallback((obj, depth = 0, keysToExpand = [], currentPath = '') => {
    const treeNodes = [];
    const now = Date.now();

    const sortedKeys = Object.keys(obj).filter(k => k !== '_topic').sort((a, b) => a.localeCompare(b));

    for (const key of sortedKeys) {
      if (obj.hasOwnProperty(key)) {

        const valuePath = currentPath ? `${currentPath}/${key}` : key;
        // Use the path as the key for stability
        const nodeKey = valuePath;
        const children = obj[key];

        const treeNode = {
          title: key,
          key: nodeKey,
        };

        if (depth < defaultExpandDepth) {
          keysToExpand.push(nodeKey);
        }

        if (children === null || typeof children === 'string' || typeof children === 'number' || typeof children === 'boolean') {
          const valueStr = children !== null ? children.toString() : 'null';
          const previousValue = previousValuesRef.current[valuePath];
          const justChanged = previousValue !== undefined && previousValue !== valueStr;
          
          // Update previous value
          previousValuesRef.current[valuePath] = valueStr;
          
          // Track when value changed
          if (justChanged) {
            recentlyChangedRef.current.set(valuePath, now);
          }
          
          // Check if value is still "hot" (changed within HIGHLIGHT_DURATION_MS)
          const changedAt = recentlyChangedRef.current.get(valuePath);
          const isHot = changedAt && (now - changedAt) < HIGHLIGHT_DURATION_MS;
          
          // Clean up old entries
          if (changedAt && (now - changedAt) >= HIGHLIGHT_DURATION_MS) {
            recentlyChangedRef.current.delete(valuePath);
          }
          
          // Truncate value dynamically based on tree depth and key name length
          const { displayValue, isTruncated } = truncateValue(valueStr, depth, key.length);
          
          // Only show tooltip if value is truncated or on touch devices
          const valueElement = (
            <span className={`node-value ${isHot ? 'node-value-changed' : ''}`}>
              {displayValue}
            </span>
          );
          
          treeNode.title = (
            <span className="node-title-container">
              <span>{key}: </span>
              {isTruncated ? (
                <Tooltip 
                  title={valueStr} 
                  placement="right" 
                  mouseEnterDelay={0.3}
                  trigger={['hover', 'click']}
                >
                  {valueElement}
                </Tooltip>
              ) : (
                valueElement
              )}
            </span>
          );
          treeNode.isLeaf = true;
        } else if (typeof children === 'object' && children !== null && !isEmptyObject(children)) {
          treeNode.children = convertToTreeData(children, depth + 1, keysToExpand, valuePath);
        } else {
          treeNode.isLeaf = true;
        }

        treeNodes.push(treeNode);
      }
    }

    return treeNodes;
  }, [truncateValue, defaultExpandDepth]);

  // Throttled function to update the tree display
  const scheduleTreeUpdate = useCallback(() => {
    if (pendingUpdateRef.current) return; // Already scheduled
    
    pendingUpdateRef.current = setTimeout(() => {
      pendingUpdateRef.current = null;
      
      const keysToExpand = [];
      const newTree = convertToTreeData(messageTreeRef.current, 0, keysToExpand, '');
      setAntTree(newTree);
      
      if (!isInitializedRef.current && keysToExpand.length > 0) {
        setExpandedKeys(keysToExpand);
        expandedKeysRef.current = keysToExpand;
        isInitializedRef.current = true;
      } else {
        const currentKeys = new Set(expandedKeysRef.current);
        const collapsed = userCollapsedKeysRef.current;
        let hasNewKeys = false;
        for (const k of keysToExpand) {
          if (!currentKeys.has(k)) {
            const underCollapsed = [...collapsed].some(
              c => k === c || k.startsWith(c + '/')
            );
            if (!underCollapsed) {
              currentKeys.add(k);
              hasNewKeys = true;
            }
          }
        }
        if (hasNewKeys) {
          const merged = Array.from(currentKeys);
          setExpandedKeys(merged);
          expandedKeysRef.current = merged;
        }
      }
      
      // Schedule a cleanup update if there are active highlights
      // This ensures highlights get cleared even if no new messages arrive
      if (recentlyChangedRef.current.size > 0) {
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = setTimeout(() => {
          highlightTimeoutRef.current = null;
          // Force a tree rebuild to clear expired highlights
          const keysToExpandCleanup = [];
          const cleanupTree = convertToTreeData(messageTreeRef.current, 0, keysToExpandCleanup, '');
          setAntTree(cleanupTree);
        }, HIGHLIGHT_DURATION_MS + 100);
      }
    }, UPDATE_THROTTLE_MS);
  }, [convertToTreeData]);

  const handleExpand = useCallback((keys) => {
    const newKeys = new Set(keys);
    const prevKeys = new Set(expandedKeysRef.current);

    for (const k of prevKeys) {
      if (!newKeys.has(k)) userCollapsedKeysRef.current.add(k);
    }
    for (const k of newKeys) {
      if (!prevKeys.has(k)) userCollapsedKeysRef.current.delete(k);
    }

    setExpandedKeys(keys);
    expandedKeysRef.current = keys;
  }, []);


  const structureMessageByTopic = useCallback((topicPath, msg) => {
    const levels = topicPath.split('/');
    const cleanedPrefix = (currentConfig.topicPrefix || '').replace(/\/+$/, '').replace(/^[#+/\s]+$/, '');
    if (!cleanedPrefix) {
      const nestedMessage = levels.reduceRight((acc, level) => ({ [level]: acc }), msg);
      return nestedMessage;
    }
    const prefixLevels = cleanedPrefix.split('/');
    const startIndex = levels.findIndex(level => level === prefixLevels[prefixLevels.length - 1]);
    const displayLevels = startIndex >= 0 ? levels.slice(startIndex + 1) : levels;

    const nestedMessage = displayLevels.reduceRight((acc, level) => {
      return { [level]: acc };
    }, msg);

    return nestedMessage;
  }, [stableTopicPrefix]);

  useEffect(() => {
    // Clean up previous connection
    if (clientRef.current) {
      clientRef.current.end(true);
      clientRef.current = null;
    }
    
    // Clear pending updates
    if (pendingUpdateRef.current) {
      clearTimeout(pendingUpdateRef.current);
      pendingUpdateRef.current = null;
    }
    
    messageTreeRef.current = {};
    previousValuesRef.current = {};
    recentlyChangedRef.current = new Map();
    isInitializedRef.current = false;
    userCollapsedKeysRef.current = new Set();
    setAntTree([]);
    setExpandedKeys([]);
    
    // Don't connect if no topics to subscribe to
    const topics = Array.isArray(topic) ? topic.filter(Boolean) : [topic].filter(Boolean);
    if (topics.length === 0) {
      return;
    }

    let isCleanedUp = false;

    console.log('🚀 UNS Sidebar MQTT Connection for:', unsId, 'Site:', selectedSite);

    const clientId = `vtec_uns_sidebar_${unsId}_${Date.now()}`;

    const client = mqtt.connect(currentConfig.broker, {
      username: currentConfig.username,
      password: currentConfig.password,
      rejectUnauthorized: false,
      connectTimeout: 30 * 1000,
      reconnectPeriod: 5 * 1000,
      keepalive: 60,
      clean: true,
      clientId: clientId,
      protocolVersion: 4,
    });
    
    clientRef.current = client;

    const connectionTimeout = setTimeout(() => {
      if (!isCleanedUp) console.error('⏰ MQTT connection timeout');
    }, 30000);

    client.on('connect', () => {
      if (isCleanedUp) return;
      clearTimeout(connectionTimeout);
      console.log('✅ UNS Sidebar connected to MQTT broker');

      topics.forEach((t) => {
        client.subscribe(t, (err) => {
          if (err) console.error('Subscription error:', err);
          else console.log('📡 Subscribed to:', t);
        });
      });
    });

    client.on('message', (topicPath, message) => {
      if (isCleanedUp) return;

      let msg;
      const rawMessage = message.toString();
      try {
        msg = JSON.parse(rawMessage);
      } catch (error) {
        msg = rawMessage;
      }

      const tree = structureMessageByTopic(topicPath, msg);
      
      mergeDeep(messageTreeRef.current, tree);
      scheduleTreeUpdate();
    });

    client.on('error', (err) => {
      if (isCleanedUp) return;
      console.error('Connection error:', err);
    });

    return () => {
      isCleanedUp = true;
      clearTimeout(connectionTimeout);
      if (pendingUpdateRef.current) {
        clearTimeout(pendingUpdateRef.current);
        pendingUpdateRef.current = null;
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }
      if (clientRef.current) {
        clientRef.current.end(true);
        clientRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsId, selectedSite, topic]); // Reconnect when site changes

  return (
    <div
      style={{
        width: '380px',
        minWidth: '380px',
        height: '100%',
        backgroundColor: 'transparent',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Flex vertical style={{ height: '100%', padding: '15px', background: 'transparent' }}>
        <Title level={5} style={{ color: '#F2EEA1', marginTop: '5px', marginBottom: '10px' }}>
          Unified Name Space
        </Title>
        
        {/* Site Filter Dropdown — hidden when no filters defined */}
        {hasFilters && (
          <Select
            value={selectedSite}
            onChange={setSelectedSite}
            options={[{ value: 'overall', label: 'Overall' }, ...effectiveSiteFilters]}
            style={{ 
              width: '100%', 
              marginBottom: '15px',
            }}
            dropdownStyle={{
              backgroundColor: '#00263e',
              zIndex: 1400,
            }}
            className="uns-site-select"
          />
        )}
        
        <Content className="customScroll" style={{ flex: 1, overflow: 'auto', background: 'transparent' }}>
          <div style={{ flex: '1 1 auto', height: '100%' }}>
            <DirectoryTree
              treeData={antTree}
              expandedKeys={expandedKeys}
              onExpand={handleExpand}
              showLine={true}
              showIcon={false}
              className={'tree'}
              selectable={false}
            />
          </div>
        </Content>
      </Flex>
    </div>
  );
};

export default UnsSidebar;

